import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, Info, HelpCircle } from 'lucide-react';
import { usePrintMode } from '../print/printMode';
import { ModalShell, Panel } from './ui/TacticalPrimitives';
import HowToRead, { ExplainerPanel } from './HowToRead';
import PillarDossierDrawer, { PillarDossierData } from './PillarDossierDrawer';

interface MetricCardProps {
  icon: LucideIcon;
  title: string;
  rating?: number; // Overall rating 0-100 for progress bar
  /**
   * Trend indicator — Comprehensive Metrics Framework panel review 2026-08-22 P0.2:
   * the previous implementation rendered "↑ Improving vs. last 30d" arrows and status tags from
   * a hardcoded up/down/neutral prop passed by callers on the strength of an ABSOLUTE score
   * threshold, not from any stored per-day pillar history. Same fabrication class as the
   * sparklines killed in the Dossier View iteration. Prop is retained as optional for
   * backwards compatibility with existing callers, but NEVER rendered until a real
   * metrics-history contract lands.
   */
  trend?: 'up' | 'down' | 'neutral';
  methodology?: string; // Methodology description for popup
  /** Data provenance: source of the metric data */
  source?: string;
  /** Data provenance: last update timestamp (ISO) */
  lastUpdated?: string;
  /** Data provenance: confidence tier or interval */
  confidence?: string;
  /** Data provenance: canonical snapshot hash (contract-pack §5) — links this card to the
   *  same snapshot the dossier and desk render. Truncated in the DATA PROVENANCE block. */
  snapshotSha?: string;
  /**
   * Per-card mini-bridge chip (contract-pack §12 + Metrics Framework P2.c): when this card
   * corresponds to a pillar in the composite bridge, pass the pillar's row so the card can
   * render a small "+X.XXpp on composite" chip near the rating — a reader sees the card's
   * pillar contribution to the composite without opening the dossier.
   */
  bridgePillar?: {
    label: string;
    contribution_pp: number;
    status: string;
  };
  /**
   * Per-card pillar-decomposition mini-bridge (Fundamentals-card panel review 2026-08-22 P1):
   * "component × weight = contribution, Σ = pillar_score". Method disclosure without visible
   * reconciliation is disallowed by the platform bridge rule. When set, renders inside the
   * modal below KEY METRICS. Unmeasured components can be included with value=null so the
   * bridge honestly declares that a weighted slice sits outside the shown measurements.
   */
  componentBridge?: {
    pillar_score: number | null;
    components: Array<{
      label: string;
      weight: number;
      value: number | null;
      contribution: number | null;
      /** Methodology transformation curve id (e.g. "vol_inverse_curve_v1"). Panel review
       *  2026-08-22 Market card P1: bridge disclosure without the transform id lets a reader
       *  see weight × value but not how the value becomes a component score. Optional for
       *  backwards compat with existing componentBridge callers. */
      curve_id?: string;
      note?: string;
    }>;
    /**
     * Coverage-weighted scoring chip (On-Chain Activity panel review 2026-08-22 P0 — the 6th
     * platform mechanism). When a methodology-declared component is not measured on this run,
     * the pillar's score is computed on a REDUCED coverage basis. This field carries the honest
     * disclosure so a reader sees "computed on X% basis — <component> absent" instead of
     * silently accepting a full-weight score sitting on a partial base.
     */
    coverage_note?: string;
  };
  metrics: Array<{
    label: string;
    value: string;
    /** Optional data-quality tier (MEASURED / MODELED / ESTIMATED / WITHHELD / SUPPRESSED)
     *  from the typed metric registry. Renders as a chip inline next to the value. */
    status?: string;
    /** Optional canonical metric key (registry key like "volatility.30d"). Kept for future
     *  cross-surface identity checks; not currently rendered — the label + status carry the
     *  visible semantics. */
    metricKey?: string;
    /** Optional key into metricExplainers — when set, a "?" affordance renders next to the
     *  row label with a plain-English What / Affects / How-to-read note. */
    explainKey?: string;
  }>;
  explanation: {
    title: string;
    description: string;
    keyPoints: string[];
    impact: string;
  };
  /** Optional key into metricExplainers for the card as a whole (the composite pillar this
   *  card represents). When set, a collapsed "How to read" note renders in the card body. */
  howToRead?: string;
  /** SR-300..310: the framework-generated pillar card contract (GET /api/live → pillar_cards). When set,
   *  the card face renders from it — score + evidence state + coverage on one level, rows as
   *  Metric | Value | Evidence ordered by methodological weight, context separated. */
  card?: PillarCardData;
  /** SR-301: no composite contribution when the composite is withheld. */
  compositeWithheld?: boolean;
  /** Framework order of the axis (CSS order inside the shared grid). */
  order?: number;
  /** Not declared by the asset's framework (SR-307) — render nothing. */
  hidden?: boolean;
  /** Standalone deep-link dossier page ("Open full dossier ↗"). */
  fullDossierHref?: string;
}

export interface PillarCardRowData { label: string; value: string; state: string; reason?: string; weight?: number }
export interface PillarCardData {
  id: string; axis_id: string; title: string; nominal_weight: number; load_bearing?: boolean;
  score: number | null; score_display: string; evidence_state: string; qualifiers?: string[];
  coverage?: number; coverage_label?: string; score_basis?: string; bands_calibrated: boolean;
  rows: PillarCardRowData[]; more_in_dossier?: number; context?: PillarCardRowData[]; methodology_id?: string;
  dossier?: PillarDossierData;
}

// Evidence chips (SR-308): semantic colour encodes EVIDENCE, not a verdict on the score.
const EVIDENCE_TONE = (state: string): string => {
  switch (state) {
    case 'MEASURED': case 'CURATED_VERIFIED': case 'OBSERVED':
      return 'border-emerald-700/60 bg-emerald-900/20 text-emerald-300';
    case 'WITHHELD': case 'NOT_APPLICABLE': case 'CONTEXT':
      return 'border-slate-600/70 bg-slate-800/40 text-slate-300';
    case 'FAILED': case 'BREACHED': case 'STALE_NOT_SCOREABLE':
      return 'border-red-800/60 bg-red-900/25 text-red-300';
    default:
      return 'border-amber-700/60 bg-amber-900/20 text-amber-300';
  }
};
const stateLabel = (s: string) => s.replace(/_/g, ' ');

// One Metric | Value | Evidence row of the card contract. The reason is a tooltip, and a small second line
// only when the row is withheld (the absence must be explained where it is seen).
const ContractRow: React.FC<{ row: PillarCardRowData }> = ({ row }) => (
  <>
    <div className="min-w-0 text-[13px] text-cmd-textMuted leading-snug" title={row.reason || undefined}>
      {row.label}
    </div>
    <div className={`text-right text-cmd-text leading-snug break-words ${/^[\d$≈~+−\-—.]/.test(row.value || '—') ? 'text-[13px] font-mono tabular-nums' : 'text-[12.5px]'}`} title={row.reason || undefined}>{row.value || '—'}</div>
    <div className="text-right">
      {row.state !== 'CONTEXT' && (
        <span className={`inline-block rounded border px-1.5 py-[1px] text-[9px] font-mono whitespace-nowrap ${EVIDENCE_TONE(row.state)}`}>{stateLabel(row.state)}</span>
      )}
    </div>
    {row.state === 'WITHHELD' && row.reason && <div className="col-span-3 -mt-1.5 text-[10.5px] text-cmd-textDim leading-snug">{row.reason}</div>}
  </>
);

// Per-status colour + border for the inline value chip. Kept in sync with the
// TypedMetricRegistryPanel status palette so a reader learns the mapping once.
const STATUS_CHIP_TONE: Record<string, string> = {
  MEASURED: 'bg-emerald-900/30 border-emerald-800/60 text-emerald-300',
  MODELED: 'bg-indigo-900/30 border-indigo-800/60 text-indigo-300',
  ESTIMATED: 'bg-amber-900/25 border-amber-800/60 text-amber-300',
  INDICATIVE: 'bg-red-900/30 border-red-800/60 text-red-300',
  WITHHELD: 'bg-slate-800/50 border-slate-700 text-slate-300',
  SUPPRESSED: 'bg-slate-800/50 border-slate-700 text-slate-300',
  // FALLBACK — value is a default (e.g. neutral 50), not a measurement. Rendered in red
  // rather than amber so a reader distinguishes "computed default" from "estimated model".
  FALLBACK: 'bg-red-900/30 border-red-800/60 text-red-300',
};

// One metric row: label (+ optional "?" explainer trigger) and value on a line, with the
// three-line explainer expanding full-width below the row when the trigger is toggled.
const ExplainRow: React.FC<{
  label: string;
  displayValue: string;
  status?: string;
  explainKey?: string;
}> = ({ label, displayValue, status, explainKey }) => {
  const [openState, setOpen] = useState(false);
  const open = usePrintMode() || openState; // printed in full (src/print/printMode.ts)
  return (
    <div>
      <div className="flex justify-between items-center">
        <span className="text-cmd-textDim text-sm inline-flex items-center gap-1.5">
          {label}
          {explainKey && (
            <button
              type="button"
              aria-expanded={open}
              aria-label={`How to read ${label}`}
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              className="inline-flex items-center justify-center w-4 h-4 text-cmd-textDim hover:text-cmd-active transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-active rounded-[2px]"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="text-cmd-text font-semibold font-mono text-sm tabular-nums">{displayValue}</span>
          {status && (
            <span
              className={`inline-flex items-center rounded border px-1.5 py-[1px] text-[9px] font-mono ${STATUS_CHIP_TONE[status] ?? 'bg-slate-800/40 border-slate-700 text-slate-300'}`}
              title={`Data-quality tier from typed_metric_registry: ${status}`}
            >
              {status}
            </span>
          )}
        </span>
      </div>
      {open && explainKey && (
        <div onClick={(e) => e.stopPropagation()}>
          <ExplainerPanel explainKey={explainKey} className="mt-2" />
        </div>
      )}
    </div>
  );
};

const MetricCard: React.FC<MetricCardProps> = ({ icon: Icon, title: titleProp, rating: ratingProp, methodology, source, lastUpdated, confidence, snapshotSha, bridgePillar, componentBridge, metrics, explanation, howToRead, card, compositeWithheld, order, hidden, fullDossierHref }) => {
  const title = card?.title || titleProp;
  const rating = card ? (card.score ?? undefined) : ratingProp;
  // The contract dossier is URL-addressable (?dossier=<pillar>): Back closes it, a shared link opens it.
  const dossierId = card?.dossier ? card.id : null;
  const urlHasDossier = () => {
    try { return !!dossierId && new URLSearchParams(window.location.search).get('dossier') === dossierId; } catch { return false; }
  };
  const [isModalOpen, setIsModalOpen] = useState<boolean>(urlHasDossier);
  const pushedRef = React.useRef(false);
  React.useEffect(() => {
    if (!dossierId) return undefined;
    if (urlHasDossier()) setIsModalOpen(true); // deep link: the card contract arrived after mount
    const onPop = () => setIsModalOpen(urlHasDossier());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);
  // Security-card panel review 2026-08-22 P0: the card badge MUST be the MIN-tier over the
  // displayed metric statuses, not a hand-passed string. DETERMINISTIC is reserved for
  // rule-table outputs (regulatory mappings, etc.) — a card with ESTIMATED sub-metrics
  // cannot ship "Data quality: DETERMINISTIC" regardless of what the caller passes.
  // Rendered confidence is min(passed_confidence, min_row_status). Callers that pass
  // 'DETERMINISTIC' with ESTIMATED rows get downgraded to ESTIMATED automatically.
  const tierOrder: Record<string, number> = { DETERMINISTIC: 5, LIVE_VERIFIED: 5, MEASURED: 4, MODELED: 3, ESTIMATED: 2, WITHHELD: 1, SUPPRESSED: 1, FALLBACK: 0 };
  const rowStatuses = (metrics ?? []).map((m) => m.status).filter((s): s is string => !!s);
  const declaredTier = (confidence ?? '').toUpperCase();
  const candidates = [declaredTier, ...rowStatuses].filter((s) => s in tierOrder);
  const renderedConfidence = candidates.length > 0
    ? candidates.reduce((worst, s) => (tierOrder[s] < tierOrder[worst] ? s : worst))
    : confidence;
  const hasProvenance = source || lastUpdated || renderedConfidence || snapshotSha;
  const formatDataAsOf = (iso?: string) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC' }) + ' UTC';
    } catch {
      return iso;
    }
  };

  const displayValue = (value: string) => {
    if (value == null || value === '' || value === 'undefined' || value === 'null' || /^\$?null/i.test(String(value))) return '—';
    return value;
  };

  const openModal = () => {
    if (dossierId && !urlHasDossier()) {
      const u = new URL(window.location.href);
      u.searchParams.set('dossier', dossierId);
      window.history.pushState({ dossier: dossierId }, '', u.toString());
      pushedRef.current = true;
    }
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    if (dossierId && urlHasDossier()) {
      if (pushedRef.current) {
        pushedRef.current = false;
        window.history.back();
      } else {
        const u = new URL(window.location.href);
        u.searchParams.delete('dossier');
        window.history.replaceState(null, '', u.toString());
      }
    }
  };

  if (hidden) return null;

  // Trend rendering (getTrendIcon / getTrendText / getTrendColor) was removed with the trend
  // block below (panel review P0.2). Restore only when a real per-day pillar history landed
  // and getTrendText can quote actual observations instead of a hardcoded phrase.

  return (
    <>
      {card ? (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        viewport={{ once: true }}
        style={order != null ? { order } : undefined}
        className="command-panel rounded-[2px] p-5 border border-[color:rgb(var(--c-border-rgb)/0.95)] hover:border-[color:rgb(var(--c-active-rgb)/0.28)] transition-colors duration-300 cursor-pointer group flex flex-col focus-visible:ring-2 focus-visible:ring-cmd-active focus-visible:ring-offset-2 focus-visible:ring-offset-cmd-bg"
        role="button"
        tabIndex={0}
        aria-label={`${title} — open dossier`}
        onClick={openModal}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(); } }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center min-w-0">
            <Icon className="w-5 h-5 text-cmd-textDim mr-2.5 shrink-0 group-hover:text-cmd-active transition-colors duration-300" />
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-cmd-textMuted group-hover:text-cmd-text transition-colors duration-300 truncate">{title}</h3>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openModal(); }}
            className="text-cmd-textDim hover:text-cmd-active transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-active rounded-[2px]"
            aria-label={`How to read ${title}`}
          >
            <Info className="w-4 h-4" />
          </button>
        </div>

        {/* SR-300: score and evidence state on ONE level. */}
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-cmd-textDim">Pillar score</div>
            <div className="font-mono tabular-nums text-cmd-text leading-none mt-1" title={card.score != null && card.score_display !== String(card.score) ? `${card.score} at computation precision; shown to the precision its evidence supports` : undefined}>
              <span className="text-[26px] font-semibold">{card.score_display}</span>
              <span className="text-[12px] text-cmd-textDim"> /100</span>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-block rounded border px-2 py-[2px] text-[10px] font-mono tracking-wide ${EVIDENCE_TONE(card.evidence_state)}`}>
              {[stateLabel(card.evidence_state), ...(card.qualifiers ?? [])].join(' · ')}
            </span>
            {card.coverage_label && <div className="mt-1 text-[10.5px] font-mono text-cmd-textMuted">{card.coverage_label}</div>}
          </div>
        </div>
        {card.score != null && (
          <div className="mt-3 w-full bg-[color:rgb(var(--c-text-mute-rgb)/0.08)] h-1 overflow-hidden rounded-[2px]" role="progressbar" aria-valuenow={card.score} aria-valuemin={0} aria-valuemax={100} aria-label={`${title} pillar score ${card.score_display} out of 100`}>
            {/* SR-308: no pillar has ratified score bands — one neutral bar, never a hidden traffic-light verdict. */}
            <div className="h-full rounded-[2px]" style={{ width: `${Math.max(0, Math.min(100, card.score))}%`, background: card.bands_calibrated ? undefined : 'rgb(var(--c-gold-rgb) / 0.7)' }} />
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px] font-mono">
          {/* SR-301: a contribution exists only on an authoritative composite. */}
          {!compositeWithheld && bridgePillar && bridgePillar.status !== 'WITHHELD' && card.score != null ? (
            <span className="rounded border border-[color:rgb(var(--c-gold-rgb)/0.35)] px-1.5 py-[1px] text-[color:var(--c-gold)]" title="(pillar score − 50) × effective weight — this pillar's contribution to the composite">
              {`${bridgePillar.contribution_pp >= 0 ? '+' : ''}${bridgePillar.contribution_pp.toFixed(2)} pts to composite`}
            </span>
          ) : null}
          <span className="text-cmd-textDim">nominal weight {Math.round(card.nominal_weight)}%{card.load_bearing ? ' · load-bearing' : ''}</span>
        </div>
        {card.score_basis && <div className="mt-2 text-[11px] text-cmd-textMuted leading-snug">{card.score_basis}</div>}

        {card.rows.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[color:rgb(var(--c-border-rgb)/0.8)] grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-3 gap-y-2 items-baseline">
            {card.rows.map((r, i) => <ContractRow key={i} row={r} />)}
          </div>
        )}
        {!!card.more_in_dossier && <div className="mt-1.5 text-[10.5px] text-cmd-textDim">+{card.more_in_dossier} more score-bearing in the dossier</div>}
        {card.context && card.context.length > 0 && (
          <div className="mt-3 pt-2 border-t border-dashed border-[color:rgb(var(--c-border-rgb)/0.9)]">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-cmd-textDim mb-1.5">Context · not scored</div>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-3 gap-y-1.5 items-baseline">
              {card.context.map((r, i) => <ContractRow key={i} row={r} />)}
            </div>
          </div>
        )}
        <div className="mt-auto pt-3">
          <div className="mt-2 pt-3 border-t border-[color:rgb(var(--c-border-rgb)/0.95)]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cmd-textMuted group-hover:text-cmd-text group-hover:underline underline-offset-4 transition-colors">Open dossier →</span>
          </div>
        </div>
      </motion.div>
      ) : (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="command-panel rounded-[2px] p-5 border border-[color:rgb(var(--c-border-rgb)/0.95)] hover:border-[color:rgb(var(--c-active-rgb)/0.28)] transition-colors duration-300 cursor-pointer group focus-visible:ring-2 focus-visible:ring-cmd-active focus-visible:ring-offset-2 focus-visible:ring-offset-cmd-bg"
        role="button"
        tabIndex={0}
        aria-label={`${title} — click for details`}
        onClick={openModal}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(); } }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
          <Icon className="w-6 h-6 text-cmd-textDim mr-3 group-hover:text-cmd-active transition-colors duration-300" />
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-cmd-textMuted group-hover:text-cmd-text transition-colors duration-300">{title}</h3>
          </div>
          {methodology != null && methodology !== '' && (
            <div
              className="flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                openModal();
              }}
            >
              <Info className="w-4 h-4 text-cmd-textDim cursor-pointer hover:text-cmd-active transition-colors" aria-label="Detailed analysis" />
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        {rating !== undefined && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs uppercase tracking-[0.12em] text-cmd-textDim">Rating</span>
              <span className="text-sm font-mono tabular-nums text-cmd-text">
                {Number(rating).toFixed(1)}/100{rating >= 100 ? ' (upper bound reached)' : ''}
              </span>
            </div>
            <div className="w-full bg-[color:rgb(var(--c-text-mute-rgb)/0.08)] h-1.5 overflow-hidden rounded-[2px]" role="progressbar" aria-valuenow={rating} aria-valuemin={0} aria-valuemax={100} aria-label={`${title} rating ${rating} out of 100`}>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${rating}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                viewport={{ once: true }}
                className="h-full rounded-[2px]"
                style={{ background: rating >= 75 ? 'rgb(var(--c-safe-rgb) / 0.85)' : rating >= 50 ? 'rgb(var(--c-alert-amber-rgb) / 0.75)' : 'rgb(var(--c-alert-red-rgb) / 0.75)' }}
              />
            </div>
          </div>
        )}
        
        {/* Trend Indicator removed — see MetricCardProps.trend for the "why" (P0.2 panel finding). */}
        {/* Mini-bridge chip — Metrics Framework P2.c. When this card corresponds to a pillar
            in the composite bridge, render "±X.XXpp on composite" so the card's contribution to
            the top-line is visible without opening the dossier. Empty when bridgePillar not
            passed (e.g. a card that isn't a pillar). */}
        {bridgePillar && (
          <div className="mb-3">
            {bridgePillar.status === 'WITHHELD' ? (
              // ARIN SEI #3: a WITHHELD pillar is NOT scored as zero — its weight is dropped and the
              // remaining pillars renormalized. Printing "+0.00pp on composite" reads as "scored at 50",
              // an absence-as-zero. Show it as withheld/not-in-composite, matching the bridge's "—".
              <span
                className="inline-flex items-center rounded border px-2 py-[1px] text-[10px] font-mono bg-gray-800/40 border-gray-600/60 text-gray-400"
                title="This pillar is WITHHELD — it carries no measured input and does NOT enter the composite. Its weight is dropped and the remaining pillars renormalized; it is not scored as zero."
              >
                — · withheld, not in composite
              </span>
            ) : (
              <span
                className={`inline-flex items-center rounded border px-2 py-[1px] text-[10px] font-mono ${bridgePillar.contribution_pp < 0 ? 'bg-red-900/25 border-red-800/60 text-red-300' : 'bg-emerald-900/25 border-emerald-800/60 text-emerald-300'}`}
                title={`This pillar's contribution to the composite rating: (score − 50) × normalized weight. Status: ${bridgePillar.status}.`}
              >
                {`${bridgePillar.contribution_pp >= 0 ? '+' : ''}${bridgePillar.contribution_pp.toFixed(2)} pts on composite`}
              </span>
            )}
          </div>
        )}

        <div className="space-y-4">
          {metrics.map((metric, index) => (
            <ExplainRow
              key={index}
              label={metric.label}
              displayValue={displayValue(metric.value)}
              status={metric.status}
              explainKey={metric.explainKey}
            />
          ))}
        </div>

        {/* Per-card "How to read" — the composite pillar this card represents. */}
        {howToRead && (
          <div className="mt-4 pt-3 border-t border-[color:rgb(var(--c-border-rgb)/0.6)]">
            <HowToRead explainKey={howToRead} content={(card as any)?.how_to_read ?? null} label="How this pillar works" />
          </div>
        )}
        <div className="mt-5 pt-3 border-t border-[color:rgb(var(--c-border-rgb)/0.95)] flex items-center justify-between gap-2">
          <span className="text-[11px] text-cmd-textDim font-medium uppercase tracking-[0.12em] group-hover:text-cmd-active transition-colors">Open dossier</span>
          {/* Trend status tag also removed with the arrows (P0.2). */}
        </div>
      </motion.div>
      )}

      {/* Contract dossier (SR-311..316): full-height drawer with URL state. */}
      {isModalOpen && card?.dossier && (
        <PillarDossierDrawer
          card={card as PillarCardData & { dossier: PillarDossierData }}
          onClose={closeModal}
          fullDossierHref={fullDossierHref}
          snapshotSha={snapshotSha}
          lastUpdated={formatDataAsOf(lastUpdated) ?? undefined}
          contribution={!compositeWithheld && bridgePillar && bridgePillar.status !== 'WITHHELD' && card.score != null
            ? `${bridgePillar.contribution_pp >= 0 ? '+' : ''}${bridgePillar.contribution_pp.toFixed(2)} pts to composite` : null}
        />
      )}
      {/* Modal — wide institutional (legacy face, pre-contract payloads) */}
      {isModalOpen && !card?.dossier && (
        <ModalShell title={explanation.title} subtitle={title} onClose={closeModal} size="xl">
          <div className="space-y-6">
            {howToRead && <ExplainerPanel explainKey={howToRead} content={(card as any)?.how_to_read ?? null} />}
            {hasProvenance && (
              <Panel variant="inset" className="p-4">
                <h3 className="section-heading mb-3">DATA PROVENANCE</h3>
                <div className="text-sm text-cmd-textMuted space-y-1 font-mono">
                  {source && <div>Source: {source}</div>}
                  {lastUpdated && formatDataAsOf(lastUpdated) && <div>Data as of: {formatDataAsOf(lastUpdated)}</div>}
                  {renderedConfidence && (
                    <div title={declaredTier && renderedConfidence !== declaredTier ? `Card badge downgraded to the MIN tier over the displayed metric rows (originally passed: ${declaredTier}) — panel-review rule: badges cannot be manually inflated above the actual row-level statuses.` : undefined}>
                      Data quality: {renderedConfidence}
                      {/* the downgrade lifecycle stays in the tooltip (reviewer metadata), not in the normal view */}
                    </div>
                  )}
                  {snapshotSha && (
                    <div title={snapshotSha}>
                      Snapshot: {snapshotSha.replace(/^sha256:/, '').slice(0, 12)}
                    </div>
                  )}
                </div>
              </Panel>
            )}

            {methodology != null && methodology !== '' && (
              <Panel variant="inset" className="p-4">
                <h3 className="section-heading mb-3">METHODOLOGY</h3>
                <p className="text-cmd-textMuted text-base leading-relaxed">{methodology}</p>
              </Panel>
            )}

            <p className="text-cmd-textMuted text-base leading-relaxed">
              {explanation.description}
            </p>

            <div>
              <h3 className="section-heading text-cmd-text mb-4">KEY METRICS</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {metrics.map((metric, index) => (
                  <Panel key={index} variant="inset" className="p-4">
                    <div className="text-cmd-textDim text-xs uppercase tracking-[0.12em]">{metric.label}</div>
                    <div className="text-cmd-text font-semibold text-lg font-mono tabular-nums mt-1">{displayValue(metric.value)}</div>
                  </Panel>
                ))}
              </div>
            </div>

            {componentBridge && Array.isArray(componentBridge.components) && componentBridge.components.length > 0 && (
              <div>
                <h3 className="section-heading text-cmd-text mb-4">PILLAR DECOMPOSITION</h3>
                <Panel variant="inset" className="p-4">
                  {componentBridge.coverage_note && (
                    <div className="mb-3 rounded border border-amber-800/60 bg-amber-900/25 px-2 py-1.5 text-[11px] font-mono text-amber-200">
                      <span className="text-amber-300">Coverage-weighted scoring:</span> {componentBridge.coverage_note}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px] font-mono">
                      <thead>
                        <tr className="text-cmd-textDim">
                          <th className="text-left font-normal py-1">Component</th>
                          <th className="text-right font-normal py-1">Weight</th>
                          <th className="text-right font-normal py-1">Value</th>
                          <th className="text-left font-normal py-1 pl-3">Curve</th>
                          <th className="text-right font-normal py-1">Contribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {componentBridge.components.map((c, i) => (
                          <tr key={i} className="border-t border-[color:rgb(var(--c-ink-rgb)/0.05)]">
                            <td className="py-1 text-cmd-text">
                              {c.label}
                              {c.note && <span className="ml-2 text-[10px] text-cmd-textDim">({c.note})</span>}
                            </td>
                            <td className="py-1 text-right text-cmd-text">{c.weight.toFixed(2)}</td>
                            <td className={`py-1 text-right ${c.value == null ? 'text-cmd-textDim' : 'text-cmd-text'}`}>
                              {c.value == null ? '—' : c.value.toFixed(2)}
                            </td>
                            <td className="py-1 pl-3 text-[10px] text-cmd-textDim">
                              {c.curve_id ?? '—'}
                            </td>
                            <td className={`py-1 text-right ${c.contribution == null ? 'text-cmd-textDim' : 'text-cmd-text'}`}>
                              {c.contribution == null ? '—' : c.contribution.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-[color:rgb(var(--c-ink-rgb)/0.1)] bg-[color:rgb(var(--c-ink-rgb)/0.05)]">
                          <td className="py-1 text-cmd-text font-semibold" colSpan={4}>Σ contributions ⇒ pillar score</td>
                          <td className="py-1 text-right text-cmd-text font-semibold">
                            {componentBridge.pillar_score == null ? '—' : componentBridge.pillar_score.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-2 text-[10px] font-mono text-cmd-textDim">
                    pillar_score = Σ (component × weight) — panel-review rule: methodology disclosure without visible reconciliation is disallowed on this platform.
                  </div>
                </Panel>
              </div>
            )}

            <div>
              <h3 className="section-heading text-cmd-text mb-4">KEY POINTS</h3>
              <ul className="space-y-2">
                {explanation.keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-cmd-active mt-2 mr-3 flex-shrink-0" />
                    <span className="text-cmd-textMuted">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Panel variant="inset" className="p-5">
              {/* Fundamentals-card panel review 2026-08-22 P0: the previous "INVESTMENT IMPACT"
                  block shipped a static advice-lexicon string ("robust long-term investment
                  potential ... active development ecosystem") regardless of the actual score.
                  Renamed to "Score interpretation" per the platform claim-boundary rule and made
                  conditional on the rating band so a low-scoring pillar cannot ship a bullish
                  reading. The static `explanation.impact` string is preserved ONLY when the
                  score falls in a band consistent with it (≥ 75); otherwise the band-conditional
                  cautionary text takes over. */}
              <h3 className="section-heading text-cmd-text mb-3">SCORE INTERPRETATION</h3>
              <p className="text-cmd-textMuted">
                {(() => {
                  const r = typeof rating === 'number' ? rating : null;
                  if (r == null) {
                    return 'Score unavailable for this asset — interpretation withheld until the pillar is measured.';
                  }
                  // SR-340: bands are uncalibrated — no qualitative band language (mid/watch/low-band).
                  if (r >= 75) {
                    return explanation.impact;
                  }
                  return `${title}: ${r.toFixed(1)}/100. Read the score against the pillar's declared methodology and decomposition — qualitative bands are not calibrated. No investment, allocation or return implication.`;
                })()}
              </p>
            </Panel>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default MetricCard;





